import React, { useEffect, useRef } from 'react';
import { useField, useFormApi } from 'informed';

import { allowedCharactersInTextFieldRegexPattern } from '../../../constants/RegexPatternConstants';
import { OutlineInfoCircleIcon } from '../../../icons/OutlineInfoCircleIcon';
import { InputType } from '../../../types';

import { ShadCNTextField } from '../../TextField';

import { FieldContainer, IconContainer } from './styledComponents';

export type InputModeTypes =
  | 'text'
  | 'email'
  | 'tel'
  | 'url'
  | 'numeric'
  | 'decimal';

export interface InformedTextFieldProps {
  field: string;
  isDisabled: boolean;
  label: string;
  fieldType: string;
  containerStyles?: any;
  inputContainerStyles?: any;
  labelStyles?: any;
  focusIcon?: any;
  shouldShowIcon: boolean;
  className?: string;
  validationSchemaObject: any;
  helperText?: string;
  touched: boolean;
  error: string;
  placeholder?: string;
  helperImageUrl?: string;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  fontColors?: any;
  helperComponent?: any;
  setSelectedSuggestedPaymentAmountIndex?: any;
  onChangeInputField?: any;
  inputFieldStyles?: any;
  mask?: any;
  maintainCursor?: any;
  parser?: any;
  formatter?: any;
  reRenderedFieldsObjects?: any;
  omegaFieldContainerStyles?: string | any;
  inputMode?: InputModeTypes;
  leftElement?: any;
  rightElement?: any;
  autoFocus?: boolean;
  handleBlur?: any;
  iconStyles?: any;
  autoComplete?: 'on' | 'off' | 'one-time-code';
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  value?: any;
  color?: string;
  leftElementContainerClassName?: string;
  isMarginBottomZero?: boolean;
  hintClassName?: string;
  inputClassName?: string;
  inputContainerClassName?: string;
  showErrorIcon?: boolean;
  maxLength?: number;
  pattern?: string;
  handleFocus?: () => void;
  onValueChange?: any;
  shouldShowLabel?: boolean;
  enableDataMask?: boolean;
  shouldShowErrorIcon?: boolean;
  dependentOnFields?: string[];
  values?: Record<string, any>;
  touchedFields?: any;
  shouldAllowSpecialCharacters?: boolean;
}

const InformedTextField = React.memo((props: InformedTextFieldProps) => {
  const {
    label,
    fieldType,
    containerStyles,
    inputContainerStyles,
    labelStyles,
    focusIcon,
    isDisabled,
    shouldShowIcon,
    className,
    validationSchemaObject,
    field,
    helperText,
    touched,
    error,
    placeholder,
    helperImageUrl,
    isHidden,
    fieldWrapperStyles,
    autoComplete,
    fontColors,
    helperComponent,
    setSelectedSuggestedPaymentAmountIndex,
    onChangeInputField,
    inputFieldStyles,
    reRenderedFieldsObjects,
    omegaFieldContainerStyles,
    leftElement,
    rightElement,
    autoFocus,
    handleBlur,
    iconStyles,
    validateOnBlur = true,
    validateOnChange = true,
    value: controlledValue,
    color,
    leftElementContainerClassName,
    isMarginBottomZero,
    hintClassName,
    inputClassName,
    inputContainerClassName,
    showErrorIcon = false,
    maxLength,
    pattern,
    handleFocus,
    onValueChange,
    shouldShowLabel = true,
    enableDataMask,
    shouldShowErrorIcon = false,
    dependentOnFields = [],
    values,
    touchedFields,
    shouldAllowSpecialCharacters = true,
    ...other
  } = props;

  const isInputFocusedRef = useRef(false);

  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    setSelectedSuggestedPaymentAmountIndex:
      setSelectedSuggestedPaymentAmountIndex,
    isDisabled,
    label,
    onValueChange: onValueChange,
    ...reRenderedFieldsObjects
  });

  const { value } = fieldState;
  const { onBlur, onChange } = informed;
  const { setTouched, setValue } = fieldApi;

  const isNotValid = touched && error ? true : false;

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isInputFocusedRef.current = false;
    onBlur(e);
    if (handleBlur) {
      handleBlur();
    }
  };

  const handleOnFocus = (): void => {
    isInputFocusedRef.current = true;
    handleFocus && handleFocus();
  };

  useEffect(() => {
    if (controlledValue !== undefined) setValue(controlledValue);
  }, [controlledValue, setValue]);

  const formApi = useFormApi();

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (dependentOnFields.length > 0) {
      let isTouched = false;
      dependentOnFields.forEach((eachField: string) => {
        if (touchedFields?.[eachField]) isTouched = true;
      });
      if (isTouched) {
        formApi.setValue(field, '');
      }
    }
  }, [...dependentOnFields.map((field) => values?.[field])]);

  const onChangeField = (value: string) => {
    const updatedValue = shouldAllowSpecialCharacters
      ? value
      : value.replace(allowedCharactersInTextFieldRegexPattern, '');
    onChangeInputField && onChangeInputField(updatedValue);
    setTouched(true);
    onChange(updatedValue);
  };

  const renderLeftElement = () => {
    const FocusIcon = focusIcon;
    return FocusIcon ? (
      <FocusIcon
        status={isInputFocusedRef.current}
        isNotValid={isNotValid && !isInputFocusedRef.current}
      />
    ) : null;
  };

  const renderErrorIcon = () => {
    return isNotValid && !isInputFocusedRef.current && showErrorIcon ? (
      <IconContainer shouldShowLabel={shouldShowLabel}>
        <OutlineInfoCircleIcon />
      </IconContainer>
    ) : null;
  };

  // Helper text is now passed to BaseTextField and handled internally
  // This aligns with InformedBranchCombobox and InformedPhoneNumberField patterns
  // BaseTextField uses single-container pattern with focus-based error hiding

  const renderTextField = () => {
    const formattedValue =
      field === 'email' && value === 'dummy#@gmail.com' ? undefined : value;

    // Determine left element to use
    let finalLeftElement: React.ReactNode | undefined;
    if (shouldShowIcon) {
      if (leftElement) {
        finalLeftElement =
          typeof leftElement === 'function' ? leftElement() : leftElement;
      } else {
        finalLeftElement = renderLeftElement();
      }
    }

    // Determine right element to use
    let finalRightElement: React.ReactNode | undefined;
    if (rightElement) {
      finalRightElement =
        typeof rightElement === 'function' ? rightElement() : rightElement;
    }

    // Pass helperText to BaseTextField to use single-container pattern
    // BaseTextField handles error, success, and helper text in a single container
    // with focus-based error hiding and inline color styles

    return (
      <ShadCNTextField
        name={field}
        label={shouldShowLabel ? label : undefined}
        value={formattedValue as string}
        type={fieldType as InputType}
        isDisabled={isDisabled}
        placeholder={placeholder}
        onChange={onChangeField}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        error={error}
        helperText={helperText}
        isRequired={false}
        leftElement={finalLeftElement}
        rightElement={finalRightElement}
        autoFocus={autoFocus}
        className={omegaFieldContainerStyles || className}
        inputClassName={inputClassName}
        labelClassName={labelStyles}
        autoComplete={autoComplete}
        maxLength={maxLength}
        pattern={pattern}
        {...other}
      />
    );
  };

  return render(
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderTextField()}
      {shouldShowErrorIcon && renderErrorIcon()}
    </FieldContainer>
  );
});

InformedTextField.displayName = 'InformedTextField';

export { InformedTextField };
